//@flow
import InfoIcon from "@mui/icons-material/Info";
import TextField from "@mui/material/TextField";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from 'react';
import { InputAdornment } from "@mui/material";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DebounceInput from "react-debounce-input";
import { QrReader } from "react-qr-reader";
import Button from "@mui/material/Button";
import { Clear } from "@mui/icons-material";
import useBadgeAction from "../../../../../../hooks/badges/useBadgeAction";
import useBadgeData from "../../../../../../hooks/badges/useBadgeData";
import { EXHIBITOR_PROFILE } from "../../../../../../locales/components/namespaces";
import InfoPopOver from "./infoPopOver";
import ScanInfo from "./scanInfo";
import UpdateVisitorDialogue from "./updateVisitorDialogue";

const ScanBarcodeInner = ({ selectedExhibition }) => {
	const { t } = useTranslation(EXHIBITOR_PROFILE)
	const { fastScanBadge, clearScannedBadge } = useBadgeAction()
	const { scanBadgeStatus } = useBadgeData()
	const [scannedText, setScannedText] = useState(null)
	const [lastScannedText, setLastScannedText] = useState(null)
	const [formOpen, setFormOpen] = useState({
		open: false,
		data: null,
		comments: ''
	})
	
	const [infoPopOver, setInfoPopOver] = useState({
		anchorEl: null,
		open: false,
		body: ''
	})
	const handleInfoPopOver = (value) => {
		setInfoPopOver({ ...value })
	}
	
	useEffect(() => {
		return () => {
			clearScannedBadge()
		}
	}, [clearScannedBadge])
	useEffect(() => {
		if (scannedText && scannedText.trim() !== "" && scannedText !== lastScannedText) {
			fastScanBadge(selectedExhibition, scannedText)
			setLastScannedText(scannedText)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scannedText, selectedExhibition])
	useEffect(() => {
		if (scanBadgeStatus && scanBadgeStatus.time) {
			setScannedText("")
			setFormOpen({
				open: true,
				data: scanBadgeStatus?.parameters,
				comments: ''
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scanBadgeStatus?.time])
	useEffect(() => {
		if (scanBadgeStatus && scanBadgeStatus.message && scanBadgeStatus.type) {
			enqueueSnackbar(
				<>
					{t(scanBadgeStatus?.message)} <br/>
					{scanBadgeStatus?.type !== 'success' ? scanBadgeStatus?.name : ''}
				</>,
				{
					variant: scanBadgeStatus?.type
				}
			)
			clearScannedBadge()
		}
	}, [clearScannedBadge, scanBadgeStatus, t])
	
	const clearScanner = () => {
		setScannedText('')
		setLastScannedText('')
		clearScannedBadge()
	}
	
	return (
		<Box
			sx={{
				'WebkitUserSelect': 'none', // Safari
				'msUserSelect': 'none', // IE 10 and IE 11
				'userSelect': 'none' // Standard syntax
			}}
		>
			<Box
				sx={{
					textAlign: 'center',
					maxWidth: 800,
					marginLeft: 'auto',
					marginRight: 'auto',
					marginTop: 'auto',
					width: '100%',
					paddingTop: '5px'
				}}
			>
				<Typography variant={"h3"}>
					{t('Scan Badge')}
					<Button
						size={"small"}
						startIcon={<InfoIcon/>}
						variant="contained"
						color="success"
						onClick={(evt) => handleInfoPopOver({
							anchorEl: evt.currentTarget,
							open: true
						})}
						sx={{
							"& .MuiButton-startIcon": {
								margin: 0,
								p: 0,
							},
							width: '0px',
							pl: 0,
							pr: 0,
							minWidth: '30px',
							ml: 1
						}}
					/>
				</Typography>
			</Box>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					textAlign: 'center',
					maxWidth: 800,
					marginLeft: 'auto',
					marginRight: 'auto',
					marginTop: '10px',
					marginBottom: '5px',
					width: '100%'
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						width: '100%',
						alignItems: 'center',
						position: 'relative',
					}}
				>
					<DebounceInput
						value={scannedText}
						debounceTimeout={800}
						onChange={evt => {
							setScannedText(evt.target.value)
						}}
						autoFocus
						style={{
							marginLeft: '1rem',
							marginRight: '1rem'
						}}
						element={(inputProps) => (
							<TextField
								{...inputProps} // forwards value/onChange on custom element input. Otherwise onChange doesn't fire
								fullWidth
								variant={"filled"}
								margin="dense"
								slotProps={{
									input: {
										endAdornment: (
											<InputAdornment position="end">
												<Button
													size={"small"}
													startIcon={<Clear/>}
													variant="contained"
													color="warning"
													onClick={(evt) => {
														clearScanner()
													}}
													sx={{
														"& .MuiButton-startIcon": {
															margin: 0,
															p: 0,
														},
														pl: 0,
														pr: 0,
														ml: 1,
													}}
												/>
											</InputAdornment>
										)
									}
								}}
							/>
						)}
					/>
				</Box>
				<QrReader
					onResult={(result, error) => {
						if (!!result) {
							setScannedText(result?.text)
						}
						if (!!error) {
							// console.info(error);
						}
					}}
					videoContainerStyle={{
						width: '320px',
						height: '320px',
						marginTop: '6px',
						position: 'relative'
					}}
					constraints={{ facingMode: 'environment' }}
					scanDelay={800}
				/>
			</Box>
			{formOpen?.open && (
				<UpdateVisitorDialogue
					formOpen={formOpen}
					selectedExhibition={selectedExhibition}
					setFormOpen={setFormOpen}
					onSuccess={() => {
						clearScanner()
					}}
				/>
			)}
			
			
			<InfoPopOver
				infoPopOver={infoPopOver}
				handleInfoPopOver={handleInfoPopOver}
				component={<ScanInfo/>}
			/>
		</Box>
	)
}

const ScanBarcode = (props) => {
	const { selectedExhibition } = props
	return (
		<>
			{selectedExhibition && (
				<ScanBarcodeInner
					selectedExhibition={selectedExhibition}
				/>
			)}
		</>
	)
}

export default ScanBarcode
